import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Settings2Icon, MessageCircle, CheckCircle, AlertCircle, RefreshCw, Mail, MessageSquareText, Phone, Settings as SettingsIcon, Loader2, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layout/AppLayout';
import VersionIndicator from '../components/common/VersionIndicator';
import { channelService } from '../services/ChannelService';
import NotificationModal from '../components/common/modals/NotificationModal';
import MobileHeader from '../layout/MobileHeader';
import CatalogSettingsPanel from '../components/features/settings/CatalogSettingsPanel';
import GenericIntegrationSetupPage from './GenericIntegrationSetupPage';
import OrgWhatsAppPanel from '../components/features/settings/OrgWhatsAppPanel';
import CRMCustomersPanel from '../components/features/settings/CRMCustomersPanel';
import { adminService } from '../services/AdminService';

// Channel configuration
interface ChannelConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  requiresQR: boolean;
  comingSoon?: boolean;
}

const getAvailableChannels = (t: any): ChannelConfig[] => [
  {
    id: 'whatsapp',
    name: t('settings.channels.whatsapp.name', 'WhatsApp'),
    description: t('settings.channels.whatsapp.description', 'Connect your WhatsApp to manage customer inquiries'),
    icon: MessageCircle,
    color: 'green',
    requiresQR: true,
  },
  {
    id: 'email',
    name: t('settings.channels.email.name', 'Email'),
    description: t('settings.channels.email.description', 'Connect your email to manage customer inquiries'),
    icon: Mail,
    color: 'blue',
    requiresQR: false,
    comingSoon: true,
  },
  {
    id: 'voice',
    name: t('settings.channels.voice.name', 'Voice'),
    description: t('settings.channels.voice.description', 'Connect voice calls to manage customer inquiries'),
    icon: Phone,
    color: 'indigo',
    requiresQR: false,
    comingSoon: true,
  },
  {
    id: 'sms',
    name: t('settings.channels.sms.name', 'SMS'),
    description: t('settings.channels.sms.description', 'Connect SMS to manage customer inquiries'),
    icon: MessageSquareText,
    color: 'purple',
    requiresQR: false,
    comingSoon: true,
  },
  {
    id: 'web-sdk',
    name: t('settings.channels.webSdk.name', 'Web (SDK)'),
    description: t('settings.channels.webSdk.description', 'Integrate web chat SDK into your website'),
    icon: MessageSquare,
    color: 'cyan',
    requiresQR: false,
    comingSoon: true,
  },
];

// Channel Settings Panel Component
interface ChannelSettingsPanelProps {
  channel: ChannelConfig;
  settings: any;
  loading: boolean;
  error: string | null;
  onUpdateSettings: (updates: any) => Promise<void>;
}

const ChannelSettingsPanel: React.FC<ChannelSettingsPanelProps> = ({
  channel,
  settings,
  loading,
  error,
  onUpdateSettings,
}) => {
  const { t } = useTranslation();
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [defaultResponse, setDefaultResponse] = useState('');
  const [responseDelay, setResponseDelay] = useState('0');
  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(false);
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('17:00');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.settings) {
      setAutoReplyEnabled(settings.settings.auto_reply_enabled || false);
      setWelcomeMessage(settings.settings.welcome_message || '');
      setDefaultResponse(settings.settings.default_response || '');
      setResponseDelay(settings.settings.response_delay?.toString() || '0');
      setWorkingHoursEnabled(settings.settings.working_hours_enabled || false);
      setWorkingHoursStart(settings.settings.working_hours_start || '09:00');
      setWorkingHoursEnd(settings.settings.working_hours_end || '17:00');
    }
  }, [settings]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await onUpdateSettings({
        enabled: settings?.enabled ?? true,
        settings: {
          auto_reply_enabled: autoReplyEnabled,
          welcome_message: welcomeMessage,
          default_response: defaultResponse,
          response_delay: parseInt(responseDelay) || 0,
          working_hours_enabled: workingHoursEnabled,
          working_hours_start: workingHoursStart,
          working_hours_end: workingHoursEnd,
        },
      });
    } catch (err) {
      // Error is handled by parent
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <h4 className="text-sm font-medium text-gray-900">{t('settings.channels.settings.title', 'Message Settings')}</h4>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-3 py-1.5 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
        >
          {saving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              {t('settings.channels.settings.saving', 'Saving...')}
            </>
          ) : (
            t('settings.channels.settings.saveChanges', 'Save changes')
          )}
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-100">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Auto Reply Toggle */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-900">{t('settings.channels.settings.autoReply.title', 'Auto-reply')}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t('settings.channels.settings.autoReply.description', 'Automatically respond to messages')}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={autoReplyEnabled}
            onChange={(e) => setAutoReplyEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
        </label>
      </div>

      {/* Welcome Message */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('settings.channels.settings.welcomeMessage.title', 'Welcome message')}
        </label>
        <textarea
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          placeholder={t('settings.channels.settings.welcomeMessage.placeholder', 'Enter a welcome message...')}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
        />
        <p className="text-xs text-gray-500 mt-1.5">{t('settings.channels.settings.welcomeMessage.description', 'Sent when starting a new conversation')}</p>
      </div>

      {/* Default Response */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('settings.channels.settings.defaultResponse.title', 'Default response')}
        </label>
        <textarea
          value={defaultResponse}
          onChange={(e) => setDefaultResponse(e.target.value)}
          placeholder={t('settings.channels.settings.defaultResponse.placeholder', 'Enter a default response...')}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
        />
        <p className="text-xs text-gray-500 mt-1.5">{t('settings.channels.settings.defaultResponse.description', 'Used when auto-reply is enabled')}</p>
      </div>

      {/* Response Delay */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-900 mb-2">
          {t('settings.channels.settings.responseDelay.title', 'Response delay (seconds)')}
        </label>
        <input
          type="number"
          min="0"
          max="60"
          value={responseDelay}
          onChange={(e) => setResponseDelay(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
        />
        <p className="text-xs text-gray-500 mt-1.5">{t('settings.channels.settings.responseDelay.description', 'Delay before sending auto-replies')}</p>
      </div>

      {/* Working Hours */}
      <div className="p-3 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-medium text-gray-900">{t('settings.channels.settings.workingHours.title', 'Working hours')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('settings.channels.settings.workingHours.description', 'Auto-reply during business hours only')}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={workingHoursEnabled}
              onChange={(e) => setWorkingHoursEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
          </label>
        </div>

        {workingHoursEnabled && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('settings.channels.settings.workingHours.startTime', 'Start time')}</label>
              <input
                type="time"
                value={workingHoursStart}
                onChange={(e) => setWorkingHoursStart(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('settings.channels.settings.workingHours.endTime', 'End time')}</label>
              <input
                type="time"
                value={workingHoursEnd}
                onChange={(e) => setWorkingHoursEnd(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
            </div>
          </div>
        )}
      </div>

      {/* Channel Enabled Toggle */}
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
        <div>
          <p className="text-sm font-medium text-gray-900">{t('settings.channels.settings.channelEnabled.title', 'Channel enabled')}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t('settings.channels.settings.channelEnabled.description', 'Enable or disable this channel')}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={settings?.enabled ?? true}
            onChange={async (e) => {
              try {
                await onUpdateSettings({
                  enabled: e.target.checked,
                  settings: settings?.settings,
                });
              } catch (err) {
                // Error handled by parent
              }
            }}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
        </label>
      </div>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();

  // Get available channels with translations
  const AVAILABLE_CHANNELS = getAvailableChannels(t);

  // Get state from URL params
  const section = (searchParams.get('section') || 'integrations') as 'integrations' | 'data-sources' | 'operations' | 'business-systems' | 'channels' | 'crm';
  const integration = searchParams.get('integration');

  // App State
  const [isLoading, setIsLoading] = useState(false);
  const [b2bCustomersEnabled, setB2bCustomersEnabled] = useState(false);

  // Fetch b2b_customers_enabled feature flag for this org
  useEffect(() => {
    const orgId = currentUser?.organization_id;
    if (!orgId) return;
    adminService.getOrgFeatureFlagPublic(orgId, 'b2b_customers_enabled')
      .then(res => setB2bCustomersEnabled(res.data?.is_enabled ?? false))
      .catch(() => setB2bCustomersEnabled(false));
  }, [currentUser?.organization_id]);

  // Channel State
  const [selectedChannel, setSelectedChannel] = useState<ChannelConfig | null>(null);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const [channelSettings, setChannelSettings] = useState<any>(null);
  const [loadingChannelSettings, setLoadingChannelSettings] = useState(false);
  const [channelSettingsError, setChannelSettingsError] = useState<string | null>(null);

  // Integration definitions
  const integrations = [
    {
      id: 'catalog',
      title: t('settings.integrations.catalog.title', 'Catalog'),
      description: t('settings.integrations.catalog.description', 'Connect to OE manufacturer catalogs for accurate part information and specifications'),
      available: true,
      category: 'data-sources'
    },
    {
      id: 'supplier',
      title: t('settings.integrations.supplier.title', 'Data Sources'),
      description: t('settings.integrations.supplier.description', 'Add your supplier data sources (CSV price lists, APIs, web crawlers) — used to look up parts data when a part is added to cart'),
      available: true,
      category: 'data-sources'
    },
    {
      id: 'quotes',
      title: t('settings.integrations.quotes.title', 'Quotes (DMS / ERP)'),
      description: t('settings.integrations.quotes.description', 'Connect to your DMS or ERP system to manage quotes and estimates'),
      available: true,
      category: 'operations'
    },
    {
      id: 'crm',
      title: t('settings.integrations.crm.title', 'Customers'),
      description: t('settings.integrations.crm.description', 'Manage the businesses your organisation sells to — contacts, tax IDs, locations and more'),
      available: b2bCustomersEnabled,
      category: 'operations',
    },
    {
      id: 'discounts',
      title: t('settings.integrations.discounts.title', 'Discount Agreements'),
      description: t('settings.integrations.discounts.description', 'Manage customer-specific pricing agreements by scope — brand, category, part number, and more'),
      available: true,
      category: 'operations',
    },
    {
      id: 'workflows',
      title: t('settings.integrations.workflows.title', 'Workflows'),
      description: t('settings.integrations.workflows.description', 'View the automation workflows configured for your organisation'),
      available: true,
      category: 'operations',
    }
  ];

  // Notification modal state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const showNotification = (title: string, message: string, type: 'success' | 'error' | 'info') => {
    setNotification({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const handleIntegrationClick = (integrationId: string) => {
    const integrationData = integrations.find(i => i.id === integrationId);
    if (integrationData?.available) {
      // Navigate directly to the discounts page
      if (integrationId === 'discounts') {
        navigate('/discounts');
        return;
      }
      // Navigate to workflows view
      if (integrationId === 'workflows') {
        navigate('/workflows');
        return;
      }
      // Navigate to CRM tab
      if (integrationId === 'crm') {
        handleSectionChange('crm');
        return;
      }
      // Show catalog settings inline
      if (integrationId === 'catalog') {
        setSearchParams({ section, integration: integrationId });
      } else if (integrationId === 'supplier' || integrationId === 'quotes') {
        setSearchParams({ section, integration: integrationId });
      } else {
        // Use generic integration setup page for other integrations
        navigate(`/integration-setup/${integrationId}`);
      }
    }
  };

  const handleSectionChange = (newSection: 'integrations' | 'data-sources' | 'operations' | 'business-systems' | 'channels' | 'crm') => {
    setSearchParams({ section: newSection });
  };

  const handleBackToIntegrations = () => {
    setSearchParams({ section: section });
  };

  // Channel Management Functions
  const handleSelectChannel = (channel: ChannelConfig) => {
    setSelectedChannel(channel);
    setChannelError(null);
    setQrCodeUrl(null);
    setConnectionStatus('disconnected');
    setIsCheckingStatus(true);
    setShowChannelSettings(false);
    setChannelSettings(null);
  };

  const handleBackToChannels = () => {
    setSelectedChannel(null);
    setChannelError(null);
    setQrCodeUrl(null);
    setConnectionStatus('disconnected');
    setShowChannelSettings(false);
    setChannelSettings(null);
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  };

  const checkChannelStatus = async () => {
    if (!selectedChannel) return;

    try {
      const status = await channelService.getChannelStatus(selectedChannel.id);
      if (status.is_connected && status.is_logged_in) {
        setConnectionStatus('connected');
        setQrCodeUrl(null);
        localStorage.setItem(`${selectedChannel.id}_connected`, 'true');
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
        }
      } else {
        if (connectionStatus === 'connected') {
          setConnectionStatus('disconnected');
          localStorage.removeItem(`${selectedChannel.id}_connected`);
        }
      }
    } catch (err) {
      console.error('Error checking status:', err);
      if (connectionStatus === 'connected') {
        setConnectionStatus('disconnected');
        localStorage.removeItem(`${selectedChannel.id}_connected`);
      }
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleChannelLogin = async () => {
    if (!selectedChannel) return;

    setChannelLoading(true);
    setChannelError(null);
    setQrCodeUrl(null);
    setConnectionStatus('connecting');

    try {
      const response = await channelService.loginToChannel(selectedChannel.id);

      if (response.alreadyConnected) {
        setConnectionStatus('connected');
        localStorage.setItem(`${selectedChannel.id}_connected`, 'true');
        setChannelLoading(false);
        return;
      }

      if (response.blob) {
        const url = URL.createObjectURL(response.blob);
        setQrCodeUrl(url);
      } else {
        throw new Error('No QR code received from server');
      }
    } catch (err: any) {
      console.error(`Error getting ${selectedChannel.name} QR code:`, err);
      setChannelError(err.response?.data?.detail || t('settings.channels.errors.generateQR', 'Failed to generate QR code. Please try again.'));
      setConnectionStatus('disconnected');
    } finally {
      setChannelLoading(false);
    }
  };

  const handleDisconnectChannel = async () => {
    if (!selectedChannel) return;

    if (!window.confirm(t('settings.channels.confirmDisconnect', 'Are you sure you want to disconnect {{channel}}?', { channel: selectedChannel.name }))) {
      return;
    }

    setChannelLoading(true);
    setChannelError(null);

    try {
      await channelService.disconnectChannel(selectedChannel.id);
      setConnectionStatus('disconnected');
      setQrCodeUrl(null);
      localStorage.removeItem(`${selectedChannel.id}_connected`);
    } catch (err: any) {
      console.error(`Error disconnecting ${selectedChannel.name}:`, err);
      setChannelError(err.response?.data?.detail || t('settings.channels.errors.disconnect', 'Failed to disconnect. Please try again.'));
    } finally {
      setChannelLoading(false);
    }
  };

  const loadChannelSettings = async () => {
    if (!selectedChannel) return;

    setLoadingChannelSettings(true);
    setChannelSettingsError(null);

    try {
      const settings = await channelService.getChannelSettings(selectedChannel.id);
      setChannelSettings(settings);
    } catch (err: any) {
      console.error('Error loading channel settings:', err);
      setChannelSettingsError(err.response?.data?.detail || t('settings.channels.errors.loadChannelSettings', 'Failed to load settings'));
    } finally {
      setLoadingChannelSettings(false);
    }
  };

  const handleToggleChannelSettings = async () => {
    if (!showChannelSettings && !channelSettings) {
      await loadChannelSettings();
    }
    setShowChannelSettings(!showChannelSettings);
  };

  const handleUpdateChannelSettings = async (updates: any) => {
    if (!selectedChannel) return;

    setLoadingChannelSettings(true);
    setChannelSettingsError(null);

    try {
      const updatedSettings = await channelService.updateChannelSettings(selectedChannel.id, updates);
      setChannelSettings(updatedSettings);
    } catch (err: any) {
      console.error('Error updating channel settings:', err);
      setChannelSettingsError(err.response?.data?.detail || t('settings.channels.errors.updateSettings', 'Failed to update settings'));
      throw err;
    } finally {
      setLoadingChannelSettings(false);
    }
  };

  // Check channel status on mount and set up polling
  useEffect(() => {
    if (!selectedChannel) return;

    const initializeConnection = async () => {
      await checkChannelStatus();

      if (connectionStatus === 'disconnected' && !qrCodeUrl && selectedChannel.requiresQR) {
        handleChannelLogin();
      }
    };

    initializeConnection();

    // Set up polling interval only if we have a selected channel
    let interval: NodeJS.Timeout | undefined;
    if (qrCodeUrl || connectionStatus === 'connected') {
      interval = setInterval(checkChannelStatus, 8000);
      setStatusCheckInterval(interval);
    }

    // Cleanup function
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [qrCodeUrl, connectionStatus, selectedChannel]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
    };
  }, [statusCheckInterval]);

  // If catalog integration is selected, show the dedicated catalog settings page
  if (integration === 'catalog') {
    return <CatalogSettingsPanel onBack={handleBackToIntegrations} />;
  }

  // If pricing integration is selected, fall through to supplier (pricing tab removed)
  // If supplier integration is selected, use the generic integration setup page
  if (integration === 'supplier' || integration === 'pricing') {
    return <GenericIntegrationSetupPage integrationId="supplier" />;
  }

  // Quotes (DMS / ERP) = the data sources marked as DMS
  if (integration === 'quotes') {
    return <GenericIntegrationSetupPage integrationId="supplier" dmsOnly />;
  }

  // Inventory tile removed — inventory-style sources (API / webcrawl) are
  // managed under Data Sources (supplier). Redirect old ?integration=inventory
  // links there so existing bookmarks keep working.
  if (integration === 'inventory') {
    return <GenericIntegrationSetupPage integrationId="supplier" />;
  }

  return (
    <AppLayout pageTitle={t('settings.title', { defaultValue: 'Settings' })}>
      <MobileHeader
        title={t('settings.title', { defaultValue: 'Settings' })}
        onBackClick={() => navigate('/chat')}
      />

      <div className="flex-1 overflow-auto bg-main-bg min-h-full">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-8 sm:py-12 lg:py-16">

          <div className="space-y-6 sm:space-y-8">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                  <span className="text-sm text-gray-500">{t('settings.loading', 'Loading settings...')}</span>
                </div>
              </div>
            ) : (
              /* Integrations Overview */
              <div className="animate-fadeIn">
                {/* Navigation Tabs */}
                <div className="flex gap-2 mb-8 pb-4 border-b border-gray-200 overflow-x-auto">
                  <button
                    onClick={() => handleSectionChange('integrations')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${section === 'integrations'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                  >
                    {t('settings.sections.allIntegrations', 'All Integrations')}
                  </button>
                  <button
                    onClick={() => handleSectionChange('data-sources')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${section === 'data-sources'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                  >
                    {t('settings.sections.dataSources', 'Data Sources')}
                  </button>
                  <button
                    onClick={() => handleSectionChange('operations')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${section === 'operations'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                  >
                    {t('settings.sections.operations', 'Operations')}
                  </button>
                </div>

                {/* Integrations Grid */}
                <div>
                  <div className="mb-6">
                    <p className="text-sm text-gray-500">
                      {t('settings.integrationsDescription', 'Connect Cata AI to your existing systems and data sources')}
                    </p>
                  </div>

                  {section === 'crm' && b2bCustomersEnabled ? (
                    <CRMCustomersPanel />
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {integrations
                      .filter(integrationItem =>
                        section === 'integrations' || integrationItem.category === section
                      )
                      .filter(integrationItem =>
                        !(integrationItem as any).adminOnly || currentUser?.role === 'admin'
                      )
                      .map(integrationItem => (
                        <div
                          key={integrationItem.id}
                          className={`group bg-white rounded-xl border border-gray-200 p-5 transition-all ${integrationItem.available
                            ? 'hover:shadow-sm hover:border-gray-300 cursor-pointer'
                            : 'opacity-60'
                            }`}
                          onClick={() => integrationItem.available && handleIntegrationClick(integrationItem.id)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="text-base font-medium text-gray-900">{integrationItem.title}</h3>
                            {integrationItem.available ? (
                              <button className="px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors">
                                {t('settings.connect', 'Connect')}
                              </button>
                            ) : (
                              <span className="px-3 py-1.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-lg">
                                {t('settings.comingSoon', 'Coming Soon')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 leading-relaxed">{integrationItem.description}</p>
                        </div>
                      ))}
                  </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-12 pt-6 border-t border-gray-200 text-center">
              <VersionIndicator className="text-gray-400" showIcon={true} size="medium" />
            </div>

          </div>
        </div>

        <NotificationModal
          isOpen={notification.isOpen}
          onClose={closeNotification}
          title={notification.title}
          message={notification.message}
          type={notification.type}
        />
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
